import type { MetricDefinition, MetricValue, UserRef } from '@ops/contracts'
import { SOURCE_LABELS, deltaBaseOf, periodoAnterior } from '@ops/contracts'
import type { ReactNode } from 'react'

import { formatMetricValue, formatPeriodo } from '../../lib/format'
import {
  Button,
  DeltaTag,
  Drawer,
  EmptyMetricState,
  HealthDot,
  KpiValor,
  ProvenanceBadge,
  Rule,
  Sparkline,
  Stack,
} from '../../ui'

/**
 * Detalle de una métrica: qué mide, con qué fórmula, contra qué umbral y de dónde
 * salió el número.
 *
 * Drawer y no modal, siguiendo el patrón de Lara: el detalle se consulta al lado
 * de la pantalla, el modal se reserva para confirmar una acción. Todo lo que
 * muestra sale del catálogo de métricas y del payload; nada está escrito acá.
 */
export interface MetricaDrawerProps {
  /** `null` = cerrado. */
  metric: MetricValue | null
  /** Definición del catálogo. `undefined` = el catálogo no cargó. */
  def: MetricDefinition | undefined
  owner: UserRef | null
  onClose: () => void
  onCargar?: (metric: MetricValue) => void
  onCrearSeguimiento: (metric: MetricValue) => void
  now: Date
}

export function MetricaDrawer({
  metric,
  def,
  owner,
  onClose,
  onCargar,
  onCrearSeguimiento,
  now,
}: MetricaDrawerProps) {
  if (metric === null) return null

  const periodoBase = periodoAnterior(metric.periodo)
  const base = periodoBase === null ? null : deltaBaseOf(metric, periodoBase)

  return (
    <Drawer
      open
      onClose={onClose}
      title={metric.nombre}
      subtitle={`Período ${formatPeriodo(metric.periodo)}`}
      footer={
        <>
          {onCargar !== undefined ? (
            <Button
              variante="secundario"
              icono="lapiz"
              onClick={() => onCargar(metric)}
            >
              Cargar un dato
            </Button>
          ) : null}
          <Button
            variante="primario"
            icono="mas"
            onClick={() => onCrearSeguimiento(metric)}
          >
            Asignar seguimiento
          </Button>
        </>
      }
    >
      <Stack gap="md">
        <div>
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <HealthDot health={metric.health} size="md" />
            <Sparkline
              valores={metric.sparkline}
              health={metric.health}
              marcarUltimo
              label={`Serie de ${metric.nombre}`}
            />
          </div>
          {metric.valor === null ? (
            <EmptyMetricState
              metricNombre={metric.nombre}
              onLoadData={
                onCargar === undefined ? undefined : () => onCargar(metric)
              }
              size="lg"
              detalle="Nadie cargó este número en el período."
            />
          ) : (
            <KpiValor as="p" valor={metric.valor} unidad={metric.unidad} />
          )}
          {metric.valor !== null ? (
            <div className="mt-1">
              <DeltaTag
                deltaPct={metric.deltaPct}
                base={base}
                unidad={metric.unidad}
                direccion={def?.direccion}
              />
            </div>
          ) : null}
          <div className="mt-2">
            <ProvenanceBadge
              provenance={
                metric.provenance.updatedAt === null ? null : metric.provenance
              }
              now={now}
              hrefAdmin="/admin"
            />
          </div>
        </div>

        <Rule spacing="none" />

        <Dato rotulo="Qué mide">
          {def?.definicion ?? 'La definición vive en el catálogo de métricas, en Admin.'}
        </Dato>

        <Dato rotulo="Fórmula">
          {metric.formula ?? def?.formula ?? 'Dato primario: no se calcula, se carga.'}
        </Dato>

        <Dato rotulo="Benchmark">
          {metric.benchmark ?? def?.benchmark ?? 'Sin benchmark definido todavía.'}
        </Dato>

        <Dato rotulo="Umbrales del semáforo">
          {def === undefined
            ? 'No pudimos leer el catálogo de métricas.'
            : `${def.direccion === 'higher_better' ? 'Más es mejor' : 'Menos es mejor'} · en target desde ${umbral(def.umbralOk, def)} · atención desde ${umbral(def.umbralWarning, def)}`}
        </Dato>

        <Dato rotulo="Fuente declarada">
          {def === undefined
            ? 'No pudimos leer el catálogo de métricas.'
            : SOURCE_LABELS[def.source]}
        </Dato>

        <Dato rotulo="Dueño">
          {owner?.nombre ?? 'Sin dueño asignado en el catálogo de métricas.'}
        </Dato>
      </Stack>
    </Drawer>
  )
}

function umbral(valor: number | null, def: MetricDefinition): string {
  return valor === null ? 'sin definir' : formatMetricValue(valor, def.unidad)
}

function Dato({
  rotulo,
  children,
}: {
  rotulo: string
  children: ReactNode
}) {
  return (
    <div className="min-w-0">
      <p className="rotulo mb-0.5">{rotulo}</p>
      <p className="text-meta text-text">{children}</p>
    </div>
  )
}
